import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/common/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AvailabilityCalendar } from "@/components/people/team/AvailabilityCalendar";
import { BalancesTable } from "@/components/people/team/BalancesTable";
import { LeaveSettingsPanel } from "@/components/people/team/LeaveSettingsPanel";
import { LeaveDecisionRow } from "@/components/people/DecisionRows";
import { canAdminPeople } from "@/lib/workspace";

function TeamRequests({ orgId, onChanged }) {
  const [status, setStatus] = useState("upcoming");
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/workspace/leave/team?org_id=${orgId}&status=${status}`).then((r) => setRows(r.data));
  useEffect(() => { setRows(null); load(); }, [orgId, status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="team-leave-requests">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="mb-3 h-9 w-[200px] bg-white" data-testid="team-leave-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="pending">Awaiting approval</SelectItem>
          <SelectItem value="upcoming">Upcoming and pending</SelectItem>
          <SelectItem value="all">Everything</SelectItem>
        </SelectContent>
      </Select>
      {rows === null ? <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" /> : rows.length === 0
        ? <EmptyState icon="Plane" title="No leave to show" description="Requests from your team appear here." testId="team-leave-empty" />
        : <div className="space-y-2">{rows.map((r) => <LeaveDecisionRow key={r.id} r={r} showStatus onChanged={() => { load(); onChanged(); }} />)}</div>}
    </div>
  );
}

export function TeamLeaveTab({ orgId, members, onChanged }) {
  const { user } = useAuth();
  const admin = canAdminPeople(user);
  const [view, setView] = useState("calendar");
  const views = [["calendar", "Availability"], ["requests", "Requests"], ["balances", "Balances"], ...(admin ? [["settings", "Settings"]] : [])];
  return (
    <div data-testid="team-leave-tab">
      <div className="mb-4 inline-flex flex-wrap gap-1 rounded-lg bg-white p-1 shadow-sm">
        {views.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`leave-view-${k}`}
            className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${view === k ? "bg-[#0B1729] text-white" : "text-[#1E2D44]/60 hover:text-[#0B1729]"}`}>{l}</button>
        ))}
      </div>
      {view === "calendar" && <AvailabilityCalendar orgId={orgId} />}
      {view === "requests" && <TeamRequests orgId={orgId} onChanged={onChanged} />}
      {view === "balances" && <BalancesTable orgId={orgId} meId={user?.id} />}
      {view === "settings" && admin && <LeaveSettingsPanel orgId={orgId} members={members} />}
    </div>
  );
}
